// Cliente de /api/oficina (diseño §8). Tipos del núcleo compartido, nunca copiados a mano.
import { api } from '../../lib/api';
import type { ArchivoDTO, ContratadoDTO, EstadoOficinaDTO, ExpedienteDTO, Potencia, ResultadoBusquedaDTO } from './compartido/vocabulario';

export interface FormularioContratar {
  /** Capturas o archivos que se mandan al agente justo después de contratarlo. */
  adjuntosPorLlegar?: number;
  caja: string;
  salaId: string;
  modelo: string;
  potencia: Potencia;
  nombre?: string;
  tema?: string;
  respuestas: { resultado?: string; paraQue?: string; comoSabre?: string };
  noTocar?: string;
  /** 04-11 — el grupo de mesas de la sala en el que se sienta (sin él, «Sin grupo»). */
  grupoId?: string;
}

/** 04-11 — crear un grupo de mesas (backend/src/oficina/grupos.ts `CrearGrupo`). `orden` solo para rehacer uno deshecho. */
export interface NuevoGrupo {
  nombre: string;
  agentIds?: string[];
  esperaTemaId?: number | null;
  orden?: number;
}
/** Lo que devuelve deshacer un grupo: lo necesario para rehacerlo igual (el «Deshacer» de 6 s). */
export interface GrupoDeshecho {
  projectId: string;
  nombre: string;
  orden: number;
  esperaTemaId: number | null;
  agentIds: string[];
}

export const oficinaApi = {
  estado: () => api<EstadoOficinaDTO>('/api/oficina/estado'),
  preferencias: () => api<{ modelo: string; potencia: Potencia }>('/api/oficina/preferencias'),
  contratar: (f: FormularioContratar, clave: string) =>
    api<{ contratado: ContratadoDTO }>('/api/oficina/contratar', { method: 'POST', body: f, headers: { 'Idempotency-Key': clave } }),
  /** 10-10 — cambiar el nombre con el que se ve al agente (solo lo cambia una persona; el agente ya no). */
  renombrar: (agentId: string, tema: string) =>
    api<{ ok: true; tema: string }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/tema`, { method: 'PATCH', body: { tema } }),
  expediente: (agentId: string) => api<ExpedienteDTO>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/expediente`),
  /** 13-10 — todas las fotos y archivos del chat, y el buscador por palabras. */
  archivos: (agentId: string) => api<{ archivos: ArchivoDTO[] }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/archivos`),
  buscar: (agentId: string, q: string) =>
    api<{ resultados: ResultadoBusquedaDTO[] }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/buscar?q=${encodeURIComponent(q)}`),
  aprobarContrato: (agentId: string, cuerpo: AprobarContrato) =>
    api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/contrato/aprobar`, { method: 'POST', body: cuerpo }),
  /** Con lo que Pau ya haya contestado en la propuesta (respuestas y «lo que ve mal»): nunca se pierde. */
  pedirCambios: (agentId: string, version: number, texto: string, ya: Omit<AprobarContrato, 'version'>) =>
    api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/contrato/cambios`, { method: 'POST', body: { version, texto, ...ya } }),
  cerrar: (agentId: string) => api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/cerrar`, { method: 'POST', body: {} }),
  seguir: (agentId: string) => api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/seguir`, { method: 'POST', body: {} }),
  forzar: (agentId: string) => api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/forzar`, { method: 'POST', body: {} }),
  despedir: (agentId: string) => api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/despedir`, { method: 'POST', body: {} }),
  /** 04-11 — aparcar (true) o despertar (false) a mano: solo se apunta la intención (compartido/aparcar.ts). */
  aparcar: (agentId: string, aparcar: boolean) => api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/aparcar`, { method: 'POST', body: { aparcar } }),
  responderPermiso: (agentId: string, permisoId: string, decision: 'aprobar' | 'denegar', nota?: string) =>
    api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/permisos/${encodeURIComponent(permisoId)}`, { method: 'POST', body: { decision, nota } }),
  sala: (projectId: string) => api<{ projectId: string; nombre: string; color: string; principal: boolean }>(`/api/oficina/salas/${encodeURIComponent(projectId)}`),
  editarSala: (projectId: string, cambios: { nombre?: string; color?: string }) =>
    api<{ nombre: string; color: string }>(`/api/oficina/salas/${encodeURIComponent(projectId)}`, { method: 'PATCH', body: cambios }),
  /** Mover a un agente de mesa (backend/src/oficina/salas.ts `OrdenMesas`). */
  ordenarMesas: (projectId: string, orden: OrdenMesas) =>
    api<{ ok: true }>(`/api/oficina/salas/${encodeURIComponent(projectId)}/mesas`, { method: 'POST', body: orden }),
  /** 04-11 — grupos de mesas («mesas de equipo»): crear (y meter a los elegidos), cambiar y deshacer. */
  crearGrupo: (projectId: string, g: NuevoGrupo) =>
    api<{ id: string }>(`/api/oficina/salas/${encodeURIComponent(projectId)}/grupos`, { method: 'POST', body: g }),
  editarGrupo: (grupoId: string, cambios: { nombre?: string; orden?: number; esperaTemaId?: number | null }) =>
    api<{ ok: true }>(`/api/oficina/grupos/${encodeURIComponent(grupoId)}`, { method: 'PATCH', body: cambios }),
  deshacerGrupo: (grupoId: string) => api<{ ok: true; deshecho: GrupoDeshecho }>(`/api/oficina/grupos/${encodeURIComponent(grupoId)}`, { method: 'DELETE' }),
  /** 04-11 (#269) — el nombre que propone la recepción para un grupo recién creado (tarda unos segundos; null si no hay). */
  nombreSugeridoGrupo: (grupoId: string) =>
    api<{ nombre?: string | null }>(`/api/oficina/grupos/${encodeURIComponent(grupoId)}/nombre-sugerido`, { method: 'POST' }),
  archivarSala: (projectId: string) => api<{ ok: true }>(`/api/oficina/salas/${encodeURIComponent(projectId)}/archivar`, { method: 'POST', body: {} }),
  reenviar: (agentId: string, messageId: string) =>
    api<{ ok: true; resultado: 'reintentado' | 'reenviado' }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/mensajes/${encodeURIComponent(messageId)}/reenviar`, { method: 'POST', body: {} }),
  responderDecision: (agentId: string, cuerpo: { preguntaId: string; opcionId?: string; texto?: string }) =>
    api<{ ok: true }>(`/api/oficina/agentes/${encodeURIComponent(agentId)}/decision`, { method: 'POST', body: cuerpo }),
};

/**
 * Mover a un agente de mesa o de grupo (espejo de `OrdenMesas` en backend/src/oficina/salas.ts; mesas automáticas desde
 * el 04-11). `grupoId`: a qué grupo va (null = «Sin grupo»; ausente = se queda en el suyo). Sin `mesa`, al primer sitio
 * libre de ese grupo.
 */
export type OrdenMesas = { accion: 'mover'; agentId: string; mesa?: number; grupoId?: string | null };

/** Lo que Pau contesta al aprobar un contrato (espejo de backend/src/oficina/respuestas.ts). */
export interface AprobarContrato {
  version: number;
  respuestas: { indice: number; respuesta?: string; decideTu?: boolean }[];
  loQueVeoMal: { indice: number; decision: 'cambialo' | 'dejalo'; nota?: string }[];
}
